'use client';

/**
 * ToolbarSearch Component
 *
 * Compact search input for DataToolbar with debouncing and clear button.
 * Designed for minimal visual weight while maintaining usability.
 */

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import type { ToolbarDensity, ToolbarSearchProps } from '../../types/dataToolbar';

// ============================================================================
// Icons
// ============================================================================

const SearchIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);

const ClearIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

// ============================================================================
// Styled Components
// ============================================================================

interface StyledTextFieldProps {
  $neonAccent?: NeonAccent;
  $isDark: boolean;
  $density: ToolbarDensity;
}

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledTextFieldProps>(({ theme, $neonAccent, $isDark, $density }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const glass = glassEffects[$isDark ? 'dark' : 'light'];
  const height = $density === 'compact' ? 32 : 36;
  const focusColor = neonStyles?.base ?? theme.palette.primary.main;

  return {
    minWidth: 160,
    maxWidth: 240,

    '& .MuiOutlinedInput-root': {
      height,
      backgroundColor: glass.background,
      backdropFilter: 'blur(12px) saturate(180%)',
      WebkitBackdropFilter: 'blur(12px) saturate(180%)',
      borderRadius: 8,
      transition: transitionPresets.input,
      fontSize: '0.8125rem',

      '& fieldset': {
        borderColor: glass.border,
        borderWidth: 0.5,
      },

      '&:hover': {
        backgroundColor: glass.backgroundHover,
      },

      '&:hover fieldset': {
        borderColor: glass.borderHover,
      },

      '&.Mui-focused fieldset': {
        borderColor: focusColor,
        borderWidth: 1.5,
      },

      '&.Mui-focused': {
        backgroundColor: glass.backgroundHover,
        boxShadow: `0 0 16px ${alpha(focusColor, 0.16)}`,
      },
    },

    '& .MuiInputBase-input': {
      padding: theme.spacing(0.5, 1),
      fontSize: '0.8125rem',
      lineHeight: 1.5,

      '&::placeholder': {
        opacity: 0.5,
        fontSize: '0.8125rem',
      },
    },

    '& .MuiInputAdornment-root': {
      marginRight: 0,
      marginLeft: theme.spacing(0.5),
    },
  };
});

const ClearButton = styled(IconButton, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  padding: 4,
  color: $isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
  transition: transitionPresets.allFast,

  '&:hover': {
    color: $isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
    backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
  },
}));

// ============================================================================
// Component
// ============================================================================

export const ToolbarSearch: React.FC<ToolbarSearchProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  debounceMs = 300,
  disabled = false,
  autoFocus = false,
  neonAccent,
  density = 'comfortable',
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Local state for immediate UI feedback
  const [localValue, setLocalValue] = useState(value);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state when external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setLocalValue(newValue);

      // Clear existing timeout
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      // Debounce the onChange callback
      if (debounceMs > 0) {
        debounceTimeout.current = setTimeout(() => {
          onChange(newValue);
        }, debounceMs);
      } else {
        onChange(newValue);
      }
    },
    [onChange, debounceMs],
  );

  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
  }, [onChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  return (
    <StyledTextField
      $neonAccent={neonAccent}
      $isDark={isDark}
      $density={density}
      value={localValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      size="small"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Box sx={{ opacity: 0.5, display: 'flex', alignItems: 'center', ml: 0.5 }}>
                <SearchIcon size={density === 'compact' ? 16 : 18} />
              </Box>
            </InputAdornment>
          ),
          endAdornment: localValue ? (
            <InputAdornment position="end">
              <ClearButton $isDark={isDark} size="small" onClick={handleClear} aria-label="Clear search">
                <ClearIcon size={density === 'compact' ? 12 : 14} />
              </ClearButton>
            </InputAdornment>
          ) : null,
        },
      }}
    />
  );
};

ToolbarSearch.displayName = 'ToolbarSearch';

export default ToolbarSearch;
